import { useMemo } from 'react';

// Helper hook that produces a stable serializable snapshot of Configure's state slices, plus
// a single applySetters() function that hydrates Configure's state from a payload.
//
// Configure currently owns ~70 useState calls. Rather than lift them into context (a Phase 3
// task), this hook simply takes the state object and the setters object that Configure passes
// in, then exposes the snapshot/apply API that useProject + payloadFormatter expect.
//
// USAGE in Configure (Phase 2F wiring):
//
//   const editorState = { polygonPoints, polygonColor, ..., clientDetails, consultantDetails };
//   const setters     = { setPolygonPoints, setPolygonColor, ..., setClientDetails, setConsultantDetails };
//   const { snapshot, applySetters } = useEditorState(editorState, setters);
//
// Then `useProject` consumes `snapshot` for serialization and `applySetters` for hydration.
export default function useEditorState(state, setters) {
  // Memoize so identity is stable while no values change — useProject can compare references
  // for "isDirty" detection without scanning every key on every render.
  const snapshot = useMemo(() => ({ ...state }), [state]);

  const applySetters = (hydrated) => {
    // Each key in `hydrated` matches a setter name in `setters` (minus the `set` prefix,
    // capitalised). e.g. hydrated.polygonPoints -> setters.setPolygonPoints.
    Object.entries(hydrated).forEach(([key, value]) => {
      const setterName = `set${key.charAt(0).toUpperCase()}${key.slice(1)}`;
      const setter = setters[setterName];
      if (typeof setter === 'function') {
        setter(value);
      }
    });
  };

  return { snapshot, applySetters };
}
